import uploadToCDN from '@/requests/cloud-storage/uploadToCDN';
import { isPicsartUrl } from '@/utils/url/safeUrl';
import { resolveLocalUrl } from './localUrlResolver';

type UploadCanvasImageParams = {
  url: string;
  imageId: string;
};

/**
 * Upload a canvas image URL to CDN.
 * If already on Picsart servers, returns the URL directly.
 * Otherwise fetches and uploads to CDN.
 */
const SAFE_URL_SCHEMES = ['http:', 'https:', 'blob:', 'data:'];

const uploadCanvasImage = async ({
  url,
  imageId,
}: UploadCanvasImageParams): Promise<string> => {
  if (isPicsartUrl(url)) {
    return url;
  }

  const resolvedUrl = resolveLocalUrl(url);

  // Validate URL scheme before fetching
  let parsed: URL;
  try {
    parsed = new URL(resolvedUrl);
  } catch {
    throw new Error(`Invalid URL for canvas image upload: ${url}`);
  }

  if (!SAFE_URL_SCHEMES.includes(parsed.protocol)) {
    throw new Error(`Unsafe URL scheme: ${parsed.protocol}`);
  }

  const response = await fetch(resolvedUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch canvas image: ${response.status}`);
  }
  const blob = await response.blob();

  const file = new File([blob], `canvas-${imageId}.png`, { type: 'image/png' });
  return uploadToCDN(file);
};

export default uploadCanvasImage;
